import type { ComponentType } from 'react'
import { BarChart3, Bell, Code, CornerDownRight, CornerUpLeft, FileText, Flag, Search, SlidersHorizontal, Sparkles, Terminal } from '@bloo-team/ui/icons'
import type { IconComponent } from '@bloo-team/ui/icons'
import type { BlockKind } from '@bloo-team/api-client'
import { CallEditor, CallView } from './CallBlock'
import { NotificationEditor, NotificationView } from './NotificationBlock'
import { OutlierEditor, OutlierView } from './OutlierBlock'
import { SearchEditor, SearchView } from './SearchBlock'
import { SignalEditor, SignalView } from './SignalBlock'
import { CodeEditor, CodeView, QueryEditor, QueryView, TextEditor, TextView } from './SimpleBlocks'
import type { BlockEditorProps, BlockViewProps } from './types'

/** What a block does — colours its icon tile (app.css `.wb-type[data-family]`). */
export type BlockFamily = 'fetch' | 'analyse' | 'detect' | 'deliver'

export interface BlockUi {
  /** The word on the card and in the insert picker. */
  label: string
  icon: IconComponent
  family: BlockFamily
  View: ComponentType<BlockViewProps> | null
  Editor: ComponentType<BlockEditorProps>
}

/** Visualization's view needs the previous task id, so the card renders it directly. */
export const BLOCK_UI: Record<Exclude<BlockKind, 'visual'>, BlockUi> & { visual: Omit<BlockUi, 'View' | 'Editor'> } = {
  dql: { label: 'DQL', icon: Terminal, family: 'fetch', View: QueryView, Editor: QueryEditor },
  sql: { label: 'SQL', icon: Terminal, family: 'fetch', View: QueryView, Editor: QueryEditor },
  gui: { label: 'Search', icon: Search, family: 'fetch', View: SearchView, Editor: SearchEditor },
  code: { label: 'Code', icon: Code, family: 'analyse', View: CodeView, Editor: CodeEditor },
  ai: { label: 'Outlier', icon: Sparkles, family: 'analyse', View: OutlierView, Editor: OutlierEditor },
  visual: { label: 'Visualization', icon: BarChart3, family: 'deliver' },
  text: { label: 'Text', icon: FileText, family: 'deliver', View: TextView, Editor: TextEditor },
  signal: { label: 'Signal', icon: Flag, family: 'detect', View: SignalView, Editor: SignalEditor },
  call: { label: 'Call', icon: CornerDownRight, family: 'deliver', View: CallView, Editor: CallEditor },
  return: { label: 'Return', icon: CornerUpLeft, family: 'deliver', View: CallView, Editor: CallEditor },
  notification: { label: 'Notification', icon: Bell, family: 'deliver', View: NotificationView, Editor: NotificationEditor },
}

/** Insert-picker order: the user's list (#205). */
export const INSERT_ORDER: BlockKind[] = [
  'dql', 'sql', 'gui', 'code', 'ai', 'visual', 'text', 'signal', 'call', 'return', 'notification',
]

export const UNKNOWN_ICON: IconComponent = SlidersHorizontal
